import type { UseFetchFnMeta } from 'hooks/useFetchFn';
import { useFetchUrl } from 'hooks/useFetchUrl';
import { isRenderingServer } from 'rendering/state/renderingState';
import { getSsrApiDataForUrl } from 'rendering/state/ssrApiData';
import { getJson } from 'utils/http/getJson';

type FetchEndpointUrlFactory<TArgs extends unknown[], TUrl extends string> = (
  ...args: TArgs
) => TUrl | undefined;

type FetchEndpointResponseMapper<TMappedResponse, TResponse> = (
  response: TResponse,
) => TMappedResponse;

export class FetchEndpoint<
  TArgs extends unknown[],
  TUrl extends string,
  TMappedResponse,
  TResponse,
> {
  urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;

  mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;

  constructor(args: {
    urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;
    mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;
  }) {
    this.urlFactory = args.urlFactory;
    this.mapResponse = args.mapResponse;
  }

  url(...args: TArgs): TUrl | undefined {
    return this.urlFactory(...args);
  }

  createSsrApiDataFn() {
    return (...args: TArgs): TMappedResponse | undefined => {
      const ssrApiData = getSsrApiDataForUrl<TResponse>(
        this.urlFactory(...args),
      );

      if (ssrApiData) return this.mapResponse(ssrApiData);
    };
  }

  createFetchFn() {
    return async (...args: TArgs): Promise<TMappedResponse> => {
      const url = this.urlFactory(...args);
      if (!url) throw new Error('Cannot fetch with undefined URL');

      // This is a server request
      if (isRenderingServer()) {
        if (process.env.IDEALIST_HORIZON_ENABLED === 'true') {
          // TODO: rely on config instead of hardcoded
          const response = await fetch(`http://web:6543/${url}`);
          return this.mapResponse(await response.json());
        }

        const data = getSsrApiDataForUrl(url);
        if (!data)
          throw new Error(`Data for ${url} is not available during SSR`);

        return this.mapResponse(data as TResponse);
      }

      return this.mapResponse(await getJson<TResponse>(url));
    };
  }

  createUseFetchHook() {
    return (
      ...args: TArgs
    ): [TMappedResponse | undefined, UseFetchFnMeta<TMappedResponse>] => {
      const url = this.url(...args);

      return useFetchUrl({ url, mapResponse: this.mapResponse });
    };
  }
}
